import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { message, Modal } from "antd";
import { BASE_API } from "../Config/Config";
import { useTranslation } from "react-i18next";

const imageExtensions = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "svg",
  "bmp",
  "avif",
];

const isImage = (name = "") => {
  return imageExtensions.includes(
    name.split(".").pop()?.toLowerCase()
  );
};

const toItems = (collections = []) => {
  const items = [];

  collections.forEach((collection, collectionIndex) => {
    const collectionId = `collection-${collectionIndex}`;

    items.push({
      id: collectionId,
      name: collection.name,
      type: "folder",
      parentId: null,
    });

    (collection.albums || []).forEach((album, albumIndex) => {
      const albumId = `${collectionId}-album-${albumIndex}`;

      items.push({
        id: albumId,
        name: album.name,
        type: "folder",
        parentId: collectionId,
      });

      (album.files || []).forEach((file, fileIndex) => {
        items.push({
          id: `${albumId}-file-${file.id ?? fileIndex}`,
          ogid: file.id,
          name: file.img,
          imageName: file.image,
          type: "file",
          parentId: albumId,
        });
      });
    });
  });

  return items;
};

const getImageUrl = (item) => {
  if (item.file) {
    return URL.createObjectURL(item.file);
  }

  if (item.imageName) {
    return `${BASE_API}/apis/${item.imageName}`;
  }

  return "";
};

export default function GalleryExplorer({
  collections,
  loading = false,
  manageable = false,
  fetchgalleryapi = () => Promise.resolve([]),
}) {
  const [items, setItems] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const fileInputRef = useRef(null);

  const { t } = useTranslation();

  useEffect(() => {
    setItems(toItems(collections));
    setCurrentFolderId(null);
    setSearch("");
  }, [collections]);

  const breadcrumbs = useMemo(() => {
    const path = [];
    let id = currentFolderId;

    while (id) {
      const item = items.find(
        (entry) => entry.id === id
      );

      if (!item) {
        break;
      }

      path.unshift(item);
      id = item.parentId;
    }

    return path;
  }, [currentFolderId, items]);

  const currentItems = useMemo(() => {
    return items.filter(
      (item) =>
        item.parentId === currentFolderId &&
        item.name
          ?.toLowerCase()
          .includes(search.trim().toLowerCase())
    );
  }, [items, currentFolderId, search]);

  // Images belong in an album: Collection > Album.
  const isAlbum = breadcrumbs.length === 2;

  const getItemPath = (item) => {
    const names = [];
    let current = item.type === "folder"
      ? item
      : items.find((entry) => entry.id === item.parentId);

    while (current) {
      names.unshift(current.name);
      current = items.find(
        (entry) => entry.id === current.parentId
      );
    }

    return names.join("/");
  };

  const openItem = (item) => {
    if (item.type === "folder") {
      setCurrentFolderId(item.id);
      setSearch("");
    } else {
      setPreview(item);
    }
  };

  const back = () => {
    const current = items.find(
      (item) => item.id === currentFolderId
    );

    setCurrentFolderId(
      current?.parentId ?? null
    );

    setSearch("");
  };

  const upload = async (event) => {
    const files = Array.from(
      event.target.files || []
    );

    if (!files.length || !currentFolderId) {
      return;
    }

    const location = breadcrumbs.map((item) => item.name).join("/");
    setSaving(true);

    try {
      await Promise.all(files.map(async (file) => {
        const formData = new FormData();
        formData.append("image", file);
        formData.append("location", location);

        const response = await fetch(
          `${BASE_API}/apis/addhomeimg.php`,
          {
            method: "POST",
            body: formData,
            credentials: "include"
          }
        );
        const result = await response.json();

        if (!response.ok || result.status === false) {
          throw new Error(result.message || "Upload failed");
        }
      }));

      setItems((previous) => [
        ...previous,
        ...files.map((file, index) => ({
          id: `local-file-${Date.now()}-${index}`,
          name: file.name,
          imageName: URL.createObjectURL(file),
          file,
          type: "file",
          parentId: currentFolderId,
        })),
      ]);
      message.success(`${files.length} image${files.length > 1 ? "s" : ""} uploaded`);
    } catch (error) {
      message.error(error.message || "Unable to upload image");
    } finally {
      setSaving(false);
      event.target.value = "";
    }
  };

  const deleteItem = async () => {
    const deleting = confirmDelete;

    if (!deleting) {
      return;
    }

    setSaving(true);

    try {
      const formData = new FormData();

      formData.append(
        "type",
        deleting.type === "folder" ? "folder" : "file"
      );

      if (deleting.type === "folder") {
        formData.append("path", getItemPath(deleting));
      }

      if (deleting.type === "file") {
        formData.append("id", deleting.ogid);
      }
      console.log(deleting)

      const response = await fetch(
        `${BASE_API}/apis/deletegalleryimg.php`,
        {
          method: "POST",
          body: formData,
          credentials: "include"
        }
      );

      const result = await response.json();

      if (!response.ok || result.status === false) {
        throw new Error(result.message || "Delete failed");
      }

      // Remove deleted item and its children from current UI
      const ids = new Set([deleting.id]);

      let changed = true;

      while (changed) {
        changed = false;

        items.forEach((item) => {
          if (
            ids.has(item.parentId) &&
            !ids.has(item.id)
          ) {
            ids.add(item.id);
            changed = true;
          }
        });
      }

      // File endpoint deletes all files in the album
      if (deleting.type === "file") {
        items
          .filter((item) => item.parentId === deleting.parentId)
          .forEach((item) => ids.add(item.id));
      }

      // Immediately remove from current UI
      setItems((previous) =>
        previous.filter((item) => !ids.has(item.id))
      );

      setConfirmDelete(null);

      // Reload gallery from backend
      await fetchgalleryapi();
      message.success("Deleted successfully");

    } catch (error) {
      message.error(error.message || "Unable to delete");
    } finally {
      setSaving(false);
    }
  };

  const count = (id) => {
    return items.filter(
      (item) => item.parentId === id
    ).length;
  };

  const title = breadcrumbs.length
    ? breadcrumbs[breadcrumbs.length - 1].name
    : t("allFiles");

  const handleCreateFolder = () => {

    const name = newFolderName.trim();

    if (!name) {
      message.warning("Please enter a folder name");
      return;
    }

    const duplicate = items.some(
      (item) =>
        item.parentId === currentFolderId &&
        item.name.toLowerCase() === name.toLowerCase()
    );

    if (duplicate) {
      message.warning(
        "A folder with this name already exists"
      );

      return;
    }

    const newFolder = {
      id: `local-folder-${Date.now()}`,
      name,
      type: "folder",
      parentId: currentFolderId,
      createdAt: new Date(),
    };

    setItems((prev) => [
      ...prev,
      newFolder,
    ]);

    setNewFolderName("");

    setCreateFolderOpen(false);

    message.success(
      "Folder created successfully"
    );
  };

  return (
    <div className="container-fluid">

      {/* Breadcrumb */}
      {breadcrumbs.length !== 0 && (

        <div className="d-flex align-items-center gap-2 mb-3">

          <button
            type="button"
            className="btn btn-light rounded-circle"
            aria-label="Go back"
            disabled={!currentFolderId}
            onClick={back}
            style={{ margin: "-20px" }}
          >
            {breadcrumbs.length !== 0 ? (
              <i className="bi bi-arrow-left" />
            ) : null}
          </button>

          <button
            type="button"
            className="btn btn-link text-decoration-none px-3 fw-semibold"
            onClick={() => {
              setCurrentFolderId(null);
              setSearch("");
            }}
          >

            <i className="bi bi-house-fill text-primary me-1" />
            {t("home")}

          </button>

          {breadcrumbs.map((item) => (
            <React.Fragment key={item.id}>

              <i className="bi bi-chevron-right text-muted small" />

              <button
                type="button"
                className="btn btn-link text-dark text-decoration-none p-0 fw-semibold"
                onClick={() => {
                  setCurrentFolderId(item.id);
                  setSearch("");
                }}
              >
                {item.name}
              </button>

            </React.Fragment>
          ))}

        </div>
      )}

      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 mx-4 mb-2">

        <div>
          <h3 className="fw-bold mb-1">
            {title}
          </h3>

          <small className="text-muted">
            {currentItems.length}{" "}
            {currentItems.length === 1
              ? t("item")
              : t("items")}
          </small>
        </div>

        <div className="d-flex gap-2">

          {/* Search */}
          <div
            className="input-group"
            style={{ maxWidth: 260 }}
          >
            <span className="input-group-text bg-white">
              <i className="bi bi-search" />
            </span>

            <input
              className="form-control"
              value={search}
              onChange={(event) =>
                setSearch(event.target.value)
              }
              placeholder={t("searchHere")}
            />
          </div>

          {/* Upload */}
          {manageable && (
            <div className="d-flex gap-2">
              {!isAlbum ? (
                <div className="col-6 col-md-auto">

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary rounded-3"
                    onClick={() =>
                      setCreateFolderOpen(
                        true
                      )
                    }
                  >
                    <i className="bi bi-folder-plus me-2"></i>
                    {t("newFolder")}
                  </button>

                </div>
              ) : (
                <div className="col-6 col-md-auto">
                  <button
                    type="button"
                    className="btn btn-sm btn-primary rounded-3"
                    onClick={() =>
                      fileInputRef.current?.click()
                    }
                    disabled={saving}
                  >
                    <i className="bi bi-cloud-arrow-up me-1" />
                    {t("upload")}
                  </button>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={upload}
                  />

                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-5 text-muted">
          {t("loadingGallery")}
        </div>
      ) : currentItems.length === 0 ? (
        <div className="text-center py-5 text-muted">
          {t("noItemsFound")}
        </div>
      ) : (
        <div className="row g-3 mx-4 mt-2">

          {currentItems.map((item) => (
            <div
              className="col-6 col-md-6 col-lg-4 col-xl-3"
              key={item.id}
            >
              <div
                className="card border-0 shadow-sm h-100 overflow-hidden"
                role="button"
                tabIndex={0}
                onClick={() =>
                  openItem(item)
                }
                onKeyDown={(event) =>
                  event.key === "Enter" &&
                  openItem(item)
                }
                style={{
                  cursor: "pointer",
                  borderRadius: 16,
                }}
              >

                {/* Preview */}
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    height: 145,
                    background:
                      item.type === "folder"
                        ? "#fff4d6"
                        : "#f4f6f8",
                  }}
                >
                  {item.type === "folder" ? (
                    <i
                      className="bi bi-folder-fill"
                      style={{
                        color: "#fbb900",
                        fontSize: 56,
                      }}
                    />
                  ) : isImage(item.name) ? (
                    <img
                      src={getImageUrl(item)}
                      alt={item.name}
                      className="w-100 h-100"
                      style={{
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <i
                      className="bi bi-file-earmark"
                      style={{
                        fontSize: 48,
                      }}
                    />
                  )}
                </div>

                {/* Card Body */}
                <div className="card-body p-3">

                  <div
                    className="fw-semibold text-truncate"
                    title={item.name}
                  >
                    {item.name}
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-2">

                    <small className="text-muted">
                      {item.type === "folder"
                        ? `${count(item.id)} ${t("items")}`
                        : t("image")}
                    </small>

                    {manageable && (
                      <button
                        type="button"
                        aria-label={`Delete ${item.name}`}
                        className="btn btn-sm text-danger p-0"
                        onClick={(event) => {
                          event.stopPropagation();
                          setConfirmDelete(item);
                        }}
                        disabled={saving}
                      >
                        <i className="bi bi-trash" />
                      </button>
                    )}

                  </div>

                </div>

              </div>
            </div>
          ))}

        </div>
      )}

      {/* Image Preview Modal */}
      {preview && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3"
          style={{
            background: "rgba(0,0,0,.72)",
            zIndex: 2000,
          }}
          onClick={() => setPreview(null)}
        >
          <div
            className="bg-white rounded-3 p-3"
            style={{
              maxWidth: "min(900px, 95vw)",
              maxHeight: "95vh",
            }}
            onClick={(event) =>
              event.stopPropagation()
            }
          >

            <div className="d-flex justify-content-between gap-3 mb-2">

              <strong className="text-truncate">
                {preview.name}
              </strong>

              <button
                type="button"
                className="btn-close"
                onClick={() =>
                  setPreview(null)
                }
              />

            </div>

            <img
              src={getImageUrl(preview)}
              alt={preview.name}
              style={{
                display: "block",
                maxWidth: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
              }}
            />

          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {confirmDelete && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3"
          style={{
            background: "rgba(0,0,0,.45)",
            zIndex: 2001,
          }}
        >
          <div
            className="bg-white rounded-3 p-4 shadow"
            style={{ maxWidth: 420 }}
          >

            <h5>
              {t("deleteItem", { name: confirmDelete.name })}
            </h5>

            <p className="text-muted mb-4">
              {confirmDelete.type === "folder"
                ? t("folderDeleteMessage")
                : t("fileDeleteMessage")}
            </p>

            <div className="d-flex justify-content-end gap-2">

              <button
                className="btn btn-light"
                onClick={() =>
                  setConfirmDelete(null)
                }
              >
                {t("cancel")}
              </button>

              <button
                className="btn btn-danger"
                onClick={deleteItem}
                disabled={saving}
              >
                {saving ? t("deleting") : t("delete")}
              </button>

            </div>

          </div>
        </div>
      )}

      <Modal
        open={createFolderOpen}
        footer={null}
        centered
        width={430}
        onCancel={() => {

          setCreateFolderOpen(false);

          setNewFolderName("");
        }}
      >

        <div className="text-center py-2">

          <div
            className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
            style={{
              width: "65px",
              height: "65px",
              background:
                "linear-gradient(135deg, #fff8e7, #fff3cd)",
            }}
          >

            <i
              className="bi bi-folder-plus"
              style={{
                fontSize: "30px",
                color: "#f4b400",
              }}
            ></i>

          </div>


          <h5 className="fw-bold mb-1">
            {t("createNewFolder")}
          </h5>

          <p className="text-muted small mb-4">
            {t("giveFolderName")}
          </p>

          <div className="text-start">

            <label className="form-label fw-semibold">
              {t("folderName")}
            </label>

            <input
              type="text"
              className="form-control form-control-lg rounded-3 shadow-none"
              placeholder={t("enterFolderName")}
              value={newFolderName}
              autoFocus
              onChange={(e) =>
                setNewFolderName(
                  e.target.value
                )
              }
              onKeyDown={(e) => {

                if (e.key === "Enter") {
                  handleCreateFolder();
                }

              }}
            />

          </div>


          <div className="d-flex justify-content-end gap-2 mt-4">

            <button
              type="button"
              className="btn btn-light rounded-3 px-4"
              onClick={() => {

                setCreateFolderOpen(
                  false
                );

                setNewFolderName("");
              }}
            >
              {t("cancel")}
            </button>


            <button
              type="button"
              className="btn btn-primary rounded-3 px-4"
              onClick={
                handleCreateFolder
              }
            >
              <i className="bi bi-folder-plus me-2"></i>

              {t("createFolder")}
            </button>

          </div>

        </div>

      </Modal>
    </div>
  );
}
